import React, { useState, useRef, createElement, Component } from 'react';
import { useTranslation } from '../../utils/i18n';
import { PackageIcon, GaugeIcon, ActivityIcon, UserIcon, ClockIcon } from 'lucide-react';
import { AthleteProfileSection } from './AthleteProfileSection';
import { NutritionProfileSection } from './NutritionProfileSection';
import { PacingModelSelector } from './PacingModelSelector';
import { ExecutionScenariosSection } from './ExecutionScenariosSection';
import { useRaceStore } from '../../stores/raceStore';
import { Card, CardContent } from '../ui/Card';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { PlusIcon, Trash2Icon, SearchIcon, DownloadIcon, UploadIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { NutritionProduct } from '../../types';
// --- Product Library Component ---
function ProductLibrary() {
  const {
    t
  } = useTranslation();
  const {
    products,
    addProduct,
    updateProduct,
    removeProduct,
    exportProducts,
    importProducts
  } = useRaceStore();
  const [search, setSearch] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [tableExpanded, setTableExpanded] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [importStatus, setImportStatus] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [newProduct, setNewProduct] = useState({
    name: '',
    unitLabel: '',
    carbsPerUnitG: 0,
    caloriesPerUnitKcal: 0,
    sodiumPerUnitMg: 0
  });
  const filtered = products.filter(p => p.name.toLowerCase().includes(search.toLowerCase()) || p.unitLabel.toLowerCase().includes(search.toLowerCase()));
  const handleAdd = () => {
    if (!newProduct.name.trim()) return;
    addProduct({
      id: crypto.randomUUID(),
      ...newProduct
    });
    setNewProduct({
      name: '',
      unitLabel: '',
      carbsPerUnitG: 0,
      caloriesPerUnitKcal: 0,
      sodiumPerUnitMg: 0
    });
    setShowAddForm(false);
  };
  const handleExport = () => {
    const json = exportProducts();
    const blob = new Blob([json], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'products.json';
    a.click();
    URL.revokeObjectURL(url);
  };
  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = event => {
      const result = importProducts(event.target?.result as string);
      setImportStatus({
        type: result.success ? 'success' : 'error',
        message: result.message
      });
      setTimeout(() => setImportStatus(null), 3000);
    };
    reader.readAsText(file);
    e.target.value = '';
  };
  return <div className="bg-white rounded-2xl border border-[#E8E8ED] shadow-sm overflow-hidden" data-id="element-1100">
      <div className="px-5 py-3.5 border-b border-[#E8E8ED]" data-id="element-1101">
        <div className="flex items-center gap-2.5" data-id="element-1102">
          <PackageIcon className="h-5 w-5 text-[#FC4C02]" data-id="element-1103" />
          <span className="text-base font-bold text-[#2D2D2D]" data-id="element-1104">
            {t('settings.productLibrary')}
          </span>
        </div>
      </div>

      <div className="p-5 space-y-4" data-id="element-1105">
        <p className="text-sm text-[#6B6B76]" data-id="element-1106">
          {t('nutrition.productLibraryDesc')}
        </p>

        <div className="flex flex-col gap-3" data-id="element-1107">
          <div className="flex flex-col sm:flex-row gap-3" data-id="element-1108">
            <div className="relative flex-1" data-id="element-1109">
              <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9B9BA7]" data-id="element-1110" />
              <input type="text" placeholder={t('settings.searchProducts')} value={search} onChange={e => setSearch(e.target.value)} className="w-full h-11 pl-10 pr-4 rounded-xl border border-[#E8E8ED] bg-[#F7F7FA] text-sm focus:ring-2 focus:ring-[#FC4C02]" data-id="element-1111" />
            </div>
            <div className="flex gap-2" data-id="element-1112">
              <button onClick={handleExport} title={t('settings.exportProducts')} className="h-11 w-11 flex items-center justify-center rounded-xl border border-[#E8E8ED] bg-[#F7F7FA] text-[#6B6B76] hover:text-[#FC4C02] hover:border-[#FC4C02] transition-colors" data-id="element-1113">
                <DownloadIcon className="h-5 w-5" data-id="element-1114" />
              </button>
              <button onClick={() => fileInputRef.current?.click()} title={t('settings.importProducts')} className="h-11 w-11 flex items-center justify-center rounded-xl border border-[#E8E8ED] bg-[#F7F7FA] text-[#6B6B76] hover:text-[#FC4C02] hover:border-[#FC4C02] transition-colors" data-id="element-1115">
                <UploadIcon className="h-5 w-5" data-id="element-1116" />
              </button>
              <input ref={fileInputRef} type="file" accept=".json" onChange={handleImport} className="hidden" data-id="element-1117" />
              <Button onClick={() => setShowAddForm(!showAddForm)} size="md" data-id="element-1118">
                <PlusIcon className="h-4 w-4 mr-2" data-id="element-1119" /> {t('settings.addProduct')}
              </Button>
            </div>
          </div>
          {importStatus && <div className={`text-xs font-semibold px-3 py-2 rounded-lg ${importStatus.type === 'success' ? 'bg-[#ECFDF5] text-[#00B843]' : 'bg-[#FEF2F2] text-[#E34402]'}`} data-id="element-1120">
              {importStatus.message}
            </div>}
        </div>

        {showAddForm && <Card data-id="element-1121">
            <CardContent className="p-5" data-id="element-1122">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-id="element-1123">
                <input placeholder={t('settings.productName')} value={newProduct.name} onChange={e => setNewProduct({
              ...newProduct,
              name: e.target.value
            })} className="h-10 px-3 rounded-xl border border-[#E8E8ED]" data-id="element-1124" />
                <input placeholder={t('settings.unit')} value={newProduct.unitLabel} onChange={e => setNewProduct({
              ...newProduct,
              unitLabel: e.target.value
            })} className="h-10 px-3 rounded-xl border border-[#E8E8ED]" data-id="element-1125" />
                <input type="number" placeholder={t('settings.carbs')} value={newProduct.carbsPerUnitG || ''} onChange={e => setNewProduct({
              ...newProduct,
              carbsPerUnitG: parseFloat(e.target.value) || 0
            })} className="h-10 px-3 rounded-xl border border-[#E8E8ED]" data-id="element-1126" />
                <input type="number" placeholder={t('settings.cals')} value={newProduct.caloriesPerUnitKcal || ''} onChange={e => setNewProduct({
              ...newProduct,
              caloriesPerUnitKcal: parseFloat(e.target.value) || 0
            })} className="h-10 px-3 rounded-xl border border-[#E8E8ED]" data-id="element-1127" />
                <input type="number" placeholder={t('settings.sodium')} value={newProduct.sodiumPerUnitMg || ''} onChange={e => setNewProduct({
              ...newProduct,
              sodiumPerUnitMg: parseFloat(e.target.value) || 0
            })} className="h-10 px-3 rounded-xl border border-[#E8E8ED]" data-id="element-1128" />
              </div>
              <div className="flex justify-end gap-2 mt-3" data-id="element-1129">
                <Button variant="ghost" size="sm" onClick={() => setShowAddForm(false)} data-id="element-1130">
                  {t('common.cancel')}
                </Button>
                <Button size="sm" onClick={handleAdd} data-id="element-1131">
                  {t('common.save')}
                </Button>
              </div>
            </CardContent>
          </Card>}

        <button onClick={() => setTableExpanded(!tableExpanded)} className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-[#E8E8ED] bg-[#F7F7FA] hover:bg-[#F0F0F5] transition-colors" data-id="element-1132">
          <span className="text-sm font-semibold text-[#6B6B76]" data-id="element-1133">
            {t('products.product')} ({filtered.length})
          </span>
          {tableExpanded ? <ChevronUpIcon className="h-4 w-4 text-[#9B9BA7]" data-id="element-1134" /> : <ChevronDownIcon className="h-4 w-4 text-[#9B9BA7]" data-id="element-1135" />}
        </button>

        {tableExpanded && <div className="rounded-xl border border-[#E8E8ED] overflow-hidden" data-id="element-1136">
            <div className="overflow-x-auto" data-id="element-1137">
              <div className="grid grid-cols-[1fr_70px_65px_65px_65px_68px] gap-2 px-5 py-3 bg-[#FAFAFA] border-b border-[#E8E8ED] text-[11px] font-semibold text-[#9B9BA7] uppercase tracking-wider min-w-[560px]" data-id="element-1138">
                <div data-id="element-1139">{t('products.product')}</div>
                <div className="text-right" data-id="element-1140">{t('products.unit')}</div>
                <div className="text-right" data-id="element-1141">{t('products.cho')}</div>
                <div className="text-right" data-id="element-1142">{t('products.cal')}</div>
                <div className="text-right" data-id="element-1143">{t('products.sodium')}</div>
                <div data-id="element-1144"></div>
              </div>
              <div className="divide-y divide-[#F0F0F5] min-w-[560px]" data-id="element-1145">
                {filtered.map(product => <div key={product.id} className="grid grid-cols-[1fr_70px_65px_65px_65px_68px] gap-2 px-5 py-3 items-center group hover:bg-[#F7F7FA]" data-id="element-1146">
                    <div className="text-sm font-medium text-[#2D2D2D] truncate" data-id="element-1147">
                      {product.name}
                    </div>
                    <div className="text-xs text-[#6B6B76] text-right" data-id="element-1148">
                      {product.unitLabel}
                    </div>
                    <div className="text-xs text-[#2D2D2D] text-right font-medium" data-id="element-1149">
                      {product.carbsPerUnitG}g
                    </div>
                    <div className="text-xs text-[#2D2D2D] text-right font-medium" data-id="element-1150">
                      {product.caloriesPerUnitKcal}
                    </div>
                    <div className="text-xs text-[#6B6B76] text-right" data-id="element-1151">
                      {product.sodiumPerUnitMg}
                    </div>
                    <div className="flex gap-1 justify-end opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1152">
                      <button onClick={() => setDeleteTarget({
                  id: product.id,
                  name: product.name
                })} className="p-1.5 text-[#9B9BA7] hover:text-red-500" data-id="element-1153">
                        <Trash2Icon className="h-4 w-4" data-id="element-1154" />
                      </button>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>}
      </div>

      <ConfirmDialog isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={() => {
      if (deleteTarget) removeProduct(deleteTarget.id);
      setDeleteTarget(null);
    }} title={t('products.removeTitle')} message={t('products.delete', {
      name: deleteTarget?.name || ''
    })} confirmLabel={t('products.removeConfirm')} variant="danger" data-id="element-1155" />
    </div>;
}
function RaceDefaults() {
  const {
    t
  } = useTranslation();
  const {
    settings,
    updateSettings
  } = useRaceStore();
  return <div className="bg-white rounded-2xl border border-[#E8E8ED] shadow-sm overflow-hidden" data-id="element-1156">
      <div className="px-5 py-3.5 border-b border-[#E8E8ED]" data-id="element-1157">
        <div className="flex items-center gap-2.5" data-id="element-1158">
          <ClockIcon className="h-5 w-5 text-[#FC4C02]" data-id="element-1159" />
          <span className="text-base font-bold text-[#2D2D2D]" data-id="element-1160">
            {t('settings.raceDefaults')}
          </span>
        </div>
      </div>

      <div className="p-5 space-y-4" data-id="element-1161">
        <p className="text-sm text-[#6B6B76]" data-id="element-1162">
          {t('settings.checkpointDefaultsDesc')}
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-1163">
          {['Water', 'Aid', 'BV', 'Other'].map(type => <Input key={type} label={t(`checkpoint.${type}`)} type="number" min={0} value={settings.defaultStopTimes?.[type as any] ?? 0} onChange={e => updateSettings({
          defaultStopTimes: {
            ...settings.defaultStopTimes,
            [type]: parseInt(e.target.value) || 0
          }
        })} data-id="element-1164" />)}
        </div>
      </div>
    </div>;
}
type SettingsSection = 'profile' | 'models' | 'scenarios' | 'products' | 'defaults';
export function SettingsTab() {
  const {
    t
  } = useTranslation();
  const [activeSection, setActiveSection] = useState<SettingsSection>('profile');
  const SECTIONS = [{
    id: 'profile',
    label: t('settings.athleteProfile'),
    icon: UserIcon
  }, {
    id: 'models',
    label: t('settings.pacingModels'),
    icon: GaugeIcon
  }, {
    id: 'scenarios',
    label: t('settings.executionStrategy'),
    icon: ActivityIcon
  }, {
    id: 'products',
    label: t('settings.productLibrary'),
    icon: PackageIcon
  }, {
    id: 'defaults',
    label: t('settings.raceDefaults'),
    icon: ClockIcon
  }];
  return <div className="space-y-6" data-id="element-1165">
      <div data-id="element-1166">
        <h2 className="text-2xl font-bold text-[#2D2D2D]" data-id="element-1167">
          {t('settings.title')}
        </h2>
        <p className="text-[#6B6B76] text-sm mt-1" data-id="element-1168">{t('settings.subtitle')}</p>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar" data-id="element-1169">
        {SECTIONS.map(section => <button key={section.id} onClick={() => setActiveSection(section.id as SettingsSection)} className={`
              flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-all
              ${activeSection === section.id ? 'bg-[#2D2D2D] text-white shadow-sm' : 'bg-white text-[#6B6B76] border border-[#E8E8ED] hover:bg-[#F7F7FA]'}
            `} data-id="element-1170">
            <section.icon className="h-4 w-4" data-id="element-1171" />
            {section.label}
          </button>)}
      </div>

      <div className="min-h-[400px]" data-id="element-1172">
        {activeSection === 'profile' && <div className="space-y-4" data-id="element-1173">
            <AthleteProfileSection data-id="element-1174" />
            <NutritionProfileSection data-id="element-1175" />
          </div>}
        {activeSection === 'models' && <PacingModelSelector data-id="element-1176" />}
        {activeSection === 'scenarios' && <ExecutionScenariosSection data-id="element-1177" />}
        {activeSection === 'products' && <ProductLibrary data-id="element-1178" />}
        {activeSection === 'defaults' && <RaceDefaults data-id="element-1179" />}
      </div>
    </div>;
}